import React, { useEffect, useMemo, useContext, useCallback, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { OverlayTrigger, Popover, Tab } from "react-bootstrap";
import WaveSurfer from "wavesurfer.js";
import { FileService } from "src/utils/file.service";
import { animated, useSpring, useTransition } from "@react-spring/web";

import { CourseContext } from "../../../context";

import Thumbnail from "src/components/thumbnail";
import { SoundVisualizer } from "src/pages/coaching/components/simulation/view/simulator/visualiser";


import { getFullName } from "src/utils/utils";
import { Media } from "src/types/media";

import { CALL_TYPES, DIFFICULTY_CHOICES, isSimulationVideoFormat } from "src/constants/simulation";

import { fetchLearnerReport } from "src/redux/actions/coaching/simulation";

import { SimulationAttempt, SimulationTranscripts } from "src/types/coaching/courses";
import { LearningRecordSimulation } from "src/types/coaching/learners";

import _ from "lodash";
import cx from "classnames";
import styles from "./callRecording.module.scss";

interface CallRecordingProps {
  learner: SimulationAttempt;
}

export const CallRecording: React.FC<CallRecordingProps> = ({ learner }) => {

  const dispatch = useDispatch();

  const courseContext = useContext(CourseContext);

  const callRecording = courseContext?.callRecording;

  const transcripts = callRecording?.transcripts ?? [];
  const loading = callRecording?.loading ?? false;

  const learnerReport = useSelector((state: any) =>
    _.get(
      state.coaching.simulations,
      `learnerReport.item`
    )
  );

  const childLearningRecord = useMemo(() => {
    return _.get(callRecording, "session.childLearningRecord", null);
  }, [callRecording]);

  const callType = useMemo(() => {
    const value = _.get(learnerReport?.simulation, "type") || "";
    return CALL_TYPES.find((item) => item.id === value)?.title || "";
  }, [learnerReport?.simulation]);

  const difficulty = useMemo(() => {
    const value = _.get(learnerReport?.simulation, "difficulty") || "";
    return DIFFICULTY_CHOICES.find((item) => item.value === value)?.label || "";
  }, [learnerReport?.simulation]);

  const handleClose = () => {
    courseContext?.closeCallRecording?.();
  };

  useEffect(() => {
    if (!learner || !childLearningRecord) {
      return;
    }

    dispatch(
      fetchLearnerReport({
        id: childLearningRecord,
      })
    );
  }, [learner?.id, childLearningRecord]);

  return (
    <div className={styles.callContainer}>
      <div className={styles.contentWrapper}>
        <div className={styles.headerContainer}>
          <div className={styles.callHeader}>
            <p className={styles.title}>call recording</p>
            <i
              className={cx("paperflite-close", styles.closeIcon)}
              onClick={handleClose}
            />
          </div>
          <div className={styles.options}>
            <div className={styles.metric}>
              <i className="paperflite-calendar" />
              <span className={styles.name}>{callType}</span>
            </div>
            {callType !== "Elevator Pitch" && (
              <div className={styles.metric}>
                <i className="paperflite-meter" />
                <span className={styles.name}>{difficulty}</span>
              </div>
            )}
          </div>
        </div>
        <div className={cx(styles.body, styles.elevatorPitch)}>
          {learnerReport && (
            <CallRecordingPlayer
              simulation={learnerReport}
              minimized={true}
              learner={learner}
              showTranscripts={callType === "Elevator Pitch"}
            />
          )}
          {callType !== "Elevator Pitch" && <CallTranscripts learner={learner} transcripts={transcripts} loading={loading} />}
        </div>
      </div>
    </div>
  );
};

interface CallTranscriptsProps {
  learner: SimulationAttempt;
  transcripts?: SimulationTranscripts[];
  loading?: boolean;
}

const CallTranscripts: React.FC<CallTranscriptsProps> = ({ learner, transcripts, loading }) => {

  const learnerProfile = useMemo(() => {
    const profile = _.get(learner, "user");
    return {
      name: getFullName(profile),
      icon: _.get(profile, "icon"),
    }
  }, [learner]);

  const learnerReport = useSelector((state: any) =>
    _.get(
      state.coaching.simulations,
      `learnerReport.item`
    )
  );

  const persona = useMemo(() => {
    return _.get(learnerReport, "simulation.persona", null);
  }, [learnerReport]);

  const personaProfile = useMemo(() => {
    return {
      name: persona?.name || "",
      icon: persona?.icon,
    };
  }, []);

  if (loading && _.size(transcripts) === 0) {
    return (
      <div className={styles.transcripts}>
        {_.times(4).map((_, index) => (
          <TranscriptRowPlaceholder key={index} />
        ))}
      </div>
    );
  }

  return (
    <div className={styles.transcripts}>
      {(transcripts || []).filter(t => t.message).map((transcript, index) => {
        const role = transcript.role;
        const speaker = role === "user" ? learnerProfile : personaProfile;
        return (
          <TranscriptRow
            key={index}
            speaker={speaker}
            text={transcript.message}
            time={transcript.secondsFromStart > 0 && FileService.formatTime(transcript.secondsFromStart)}
          />
        )
      })}
    </div>
  );
}


interface TranscriptRowProps {
  speaker: {
    name: string;
    icon?: Media;
  };
  text: string;
  time: string | boolean;
}

const TranscriptRow: React.FC<TranscriptRowProps> = ({ speaker, text, time }) => {
  return (
    <div className={styles.transcriptRow}>
      {time && <div className={styles.time}>{time}</div>}
      <div className={styles.speaker}>
        <Thumbnail className={styles.icon} media={speaker.icon} defaultImage="/images/assets/simulationThumbnail.jpg" />
        <div className={styles.name}>{speaker.name}</div>
      </div>
      <div className={styles.text}>{text}</div>
    </div>
  )
}

const TranscriptRowPlaceholder: React.FC = () => {
  return (
    <div className={cx(styles.transcriptRow, styles.placeholder)}>
      <div className={styles.speaker}>
        <div className={cx(styles.icon, styles.block)} />
        <div className={cx(styles.name, styles.block)}></div>
      </div>
      <div className={cx(styles.text)}>
        <div className={cx(styles.block)}></div>
        <div className={cx(styles.block)}></div>
      </div>
    </div>
  );
}

interface CallRecordingPlayerProps {
  simulation: LearningRecordSimulation;
  learner: SimulationAttempt;
  onPlay?: () => void;
  minimized?: boolean;
  showTranscripts?: boolean;
  className?: any;
  callRecordingStyle?: any;
  mediaStyle?: any;
}

export const CallRecordingPlayer: React.FC<CallRecordingPlayerProps> = ({
  simulation,
  learner,
  onPlay,
  minimized,
  showTranscripts,
  className,
  callRecordingStyle,
  mediaStyle
}) => {

  const containerRef = React.useRef<HTMLDivElement>(null);
  const playerRef = React.useRef<WaveSurfer | null>(null);
  const videoRef = React.useRef<HTMLVideoElement | null>(null);

  const userAvatar = _.get(learner, 'user.icon.full', '');
  const initials = `${(_.get(learner.user, 'firstName', '')[0] || '')}${(_.get(learner.user, 'lastName', '')[0] || '')}`.toUpperCase();

  const getMediaType = (url: string): 'video' | 'audio' => {
    return url.endsWith('.mp4') ? 'video' : 'audio';
  };
  
  const { url, mediaType } = useMemo(() => {
    const url = _.get(simulation, 'report.callRecordingUrl') as string | undefined;
    const mediaType = url ? getMediaType(url) : null;
    return { url, mediaType };
  }, [simulation]);

  const [playerState, setPlayerState] = useState({
    muted: false,
    playbackRate: 1,
    volume: 100,
    duration: 0,
    currentTime: 0,
    waveformLoaded: false,
    audioPlaying: false,
  });

  const loadingTransition = useTransition(!playerState.waveformLoaded, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  const [playerStyles] = useSpring({
    transform: playerState.waveformLoaded ? "translateY(0%)" : "translateY(100%)",
    opacity: playerState.waveformLoaded ? 1 : 0,
  }, [playerState.waveformLoaded]);

  const handleVolumeChange = useCallback((volume) => {
    if (playerRef.current) {
      playerRef.current.setVolume(volume / 100);
      setPlayerState((state) => {
        return {
          ...state,
          volume: volume
        }
      });
    }
  }, [playerRef.current]);

  const initWaveSurfer = useCallback((url: string, mediaEl?: HTMLVideoElement) => {
    if (playerRef.current) {
      try { playerRef.current.destroy(); } catch (e) { console.error(e); }
      playerRef.current = null;
    }

    const ctx = document.createElement('canvas').getContext('2d')!;
    const gradient = ctx.createLinearGradient(0, 0, 0, 150);
    gradient.addColorStop(0, '#FF284F');
    gradient.addColorStop(0.15, '#FF439B');
    gradient.addColorStop(0.44, '#FF45CF');
    gradient.addColorStop(0.83, '#00A5B9');
    gradient.addColorStop(1, '#FF45CF');

    playerRef.current = WaveSurfer.create({
      container: containerRef.current!,
      height: 14,
      waveColor: '#fff',
      progressColor: '#fff',
      barWidth: 3,
      barGap: 2,
      barRadius: 16,
      cursorWidth: 0,
      normalize: true,
      hideScrollbar: true,
      ...(mediaEl ? { media: mediaEl } : {}),
    });

    if (!mediaEl) {
      playerRef.current.load(url);
    }

    playerRef.current.on('ready', () => {
      setPlayerState(s => ({
        ...s,
        duration: Math.round(playerRef.current?.getDuration() || 0),
        waveformLoaded: true,
      }));
      playerRef.current?.setTime(0);

      if (videoRef.current) {
        videoRef.current.currentTime = 0.01;
      }
    });
    playerRef.current.on('play', () =>
      setPlayerState(s => ({ ...s, audioPlaying: playerRef.current?.isPlaying() || false }))
    );
    playerRef.current.on('pause', () =>
      setPlayerState(s => ({ ...s, audioPlaying: playerRef.current?.isPlaying() || false }))
    );
    playerRef.current.on('audioprocess', () =>
      setPlayerState(s => ({ ...s, currentTime: Math.round(playerRef.current?.getCurrentTime() || 0) }))
    );
    playerRef.current.on('seeking', () =>
      setPlayerState(s => ({ ...s, currentTime: playerRef.current?.getCurrentTime() || 0 }))
    );
    playerRef.current.on('finish', () =>
      setPlayerState(s => ({ ...s, audioPlaying: false, currentTime: 0 }))
    );
    playerRef.current.on('error', e =>
      setPlayerState(s => ({ ...s, error: e } as any))
    );
  }, []);

  useEffect(() => {
    if (!url || mediaType !== 'audio' || !WaveSurfer) return;
    initWaveSurfer(url);
    return () => {
      try { playerRef.current?.destroy(); } catch (e) { console.error(e); }
      playerRef.current = null;
    };
  }, [url, mediaType, initWaveSurfer]);

  useEffect(() => {
    if (playerState.audioPlaying && onPlay) onPlay();
  }, [playerState.audioPlaying]);

  const onPlayPause = useCallback(() => {
    playerRef.current?.playPause();
  }, []);

  const handleSkipBackward = useCallback(() => {
    if (playerRef.current) {
      const t = Math.max(playerRef.current.getCurrentTime() - 15, 0);
      playerRef.current.seekTo(t / playerRef.current.getDuration());
    }
  }, []);

  const handleSkipForward = useCallback(() => {
    if (playerRef.current) {
      const dur = playerRef.current.getDuration();
      const t = Math.min(playerRef.current.getCurrentTime() + 10, dur);
      playerRef.current.seekTo(t / dur);
    }
  }, []);

  const handlePlaybackRateChange = useCallback((rate) => {
    if (playerRef.current) {
      playerRef.current.setPlaybackRate(rate);
      setPlayerState((state) => {
        return {
          ...state,
          playbackRate: rate
        }
      });
    }
  }, [playerRef.current]);

  return (
    <div className={cx(styles.callRecordingPlayer, { [styles.minimized]: minimized }, className)}>
      <div className={styles.mediaContainer}>

        {loadingTransition((style, item) =>
          item && (
            <animated.div className={styles.loading} style={style}>
              <AudioVisualizerLoader />
            </animated.div>
          )
        )}
        <>
          <div className={styles.leftActions}>
            <VolumeButton volume={playerState.volume} onChange={handleVolumeChange} />
            <PlayRateButton playbackRate={playerState.playbackRate} onChange={handlePlaybackRateChange} />
          </div>
          <div className={styles.centerActions}>
            <button onClick={handleSkipBackward} className={cx(styles.action, styles.rewind)}>
              <i className="paperflite-reload" /><span>15</span>
            </button>
            <button onClick={onPlayPause} className={cx(styles.action, styles.play)}>
              <i className={playerState.audioPlaying ? 'paperflite-pause' : 'paperflite-play'} />
            </button>
            <button onClick={handleSkipForward} className={cx(styles.action, styles.forward)}>
              <i className="paperflite-reload" /><span>10</span>
            </button>
          </div>
        </>

        {mediaType === 'video' && url && (
          <video
            ref={videoRef}
            src={url}
            crossOrigin="anonymous"
            className={cx(styles.videoPlayer, { [styles.minimized]: minimized, mediaStyle })}
            style={{ visibility: playerState.waveformLoaded ? 'visible' : 'hidden' }}
            onLoadedMetadata={() => {
              if (WaveSurfer && videoRef.current) {
                initWaveSurfer(url, videoRef.current);
              }
            }}
          />
        )}

        {mediaType === 'audio' && (
          <div className={cx(styles.audioAvatar, { [styles.minimized]: minimized, [styles.noAvatar]: !userAvatar }, callRecordingStyle)}>
            {userAvatar && (
              <img
                src={userAvatar}
                className={cx(styles.avatarImage, mediaStyle)}
                alt="speaker"
              />
            )}
            {!userAvatar && (
              <div className={cx(styles.initialsAvatar, { [styles.minimized]: minimized })}>
                {initials}
              </div>
            )}
          </div>
        )}
        <div className={styles.playerContainer}>
          <animated.div className={styles.player} style={playerStyles}>
            <div className={styles.track}>
              <div ref={containerRef} className={styles.waveform} />
            </div>
          </animated.div>
        </div>
      </div>
      {showTranscripts && (
        <div className={styles.right}>
          <Transcripts simulation={simulation} />
        </div>
      )}
    </div>
  );
};

const AudioVisualizerLoader: React.FC = () => (
  <div className={styles.audioVisualizerLoader}>
    <SoundVisualizer
      disabled={false}
      volumeLevel={1}
      barCount={120}
      barWidth="2px"
      height={30}
      color="#FF45C2"
      durationInSeconds={0.25}
      cornerRadius={8}
      verticalAlignment="center"
    />
    <div className={styles.message}>Analyzing call recording...</div>
  </div>
);

interface VolumeButtonProps {
  volume: number;
  // eslint-disable-next-line no-unused-vars
  onChange: (value: number) => void;
}

const VolumeButton: React.FC<VolumeButtonProps> = ({ volume, onChange }) => {
  const volumePopover = useMemo(() => {
    const handleVolumeChange = (e) => {
      if (onChange) {
        onChange(parseInt(e.target.value));
      }
    };

    return (
      <Popover id="volume-popup" className={styles.volumePopover}>
        <Popover.Body>
          <div className={styles.volumeControl}>
            <div className={styles.progress} style={{
              height: `calc(${volume}% + ${(volume > 25 ? 0 : 4)}px)`,
              borderTopLeftRadius: volume > 50 ? 4 : 0,
              borderTopRightRadius: volume > 50 ? 4 : 0,
            }} />
            <input type="range"
              min="0" max="100"
              value={volume}
              className={styles.volumeSlider}
              onChange={handleVolumeChange}
            />
          </div>
        </Popover.Body>
      </Popover>
    );

  }, [volume, onChange]);

  return (
    <OverlayTrigger trigger="click" placement="top" overlay={volumePopover} rootClose>
      <button className={cx(styles.action, styles.volume)}>
        <i className="paperflite-volume-on"></i>
      </button>
    </OverlayTrigger>
  );
}

interface PlayRateButtonProps {
  playbackRate: number;
  // eslint-disable-next-line no-unused-vars
  onChange: (value: number) => void;
}

const PlayRateButton: React.FC<PlayRateButtonProps> = ({ playbackRate, onChange }) => {
  const playRatePopover = useMemo(() => {
    const handleRateChange = (rate: number) => {
      if (onChange) {
        onChange(rate);
      }
    };

    return (
      <Popover id="playrate-popup" className={styles.playRatePopover}>
        <Popover.Body>
          <div className={styles.options}>
            <div
              className={cx(styles.option, { [styles.selected]: playbackRate === 0.5 })}
              onClick={() => handleRateChange(0.5)}
            >
              <span className={styles.label}>0.5X</span>
              {playbackRate === 0.5 && <i className="paperflite-tick-selected"></i>}
            </div>
            <div
              className={cx(styles.option, { [styles.selected]: playbackRate === 1 })}
              onClick={() => handleRateChange(1.0)}
            >
              <span className={styles.label}>1.0X</span>
              {playbackRate === 1 && <i className="paperflite-tick-selected"></i>}
            </div>
            <div
              className={cx(styles.option, { [styles.selected]: playbackRate === 1.5 })}
              onClick={() => handleRateChange(1.5)}
            >
              <span className={styles.label}>1.5X</span>
              {playbackRate === 1.5 && <i className="paperflite-tick-selected"></i>}
            </div>
            <div
              className={cx(styles.option, { [styles.selected]: playbackRate === 2 })}
              onClick={() => handleRateChange(2.0)}
            >
              <span className={styles.label}>2.0X</span>
              {playbackRate === 2.0 && <i className="paperflite-tick-selected"></i>}
            </div>
          </div>
        </Popover.Body>
      </Popover>
    );

  }, [playbackRate, onChange]);

  return (
    <OverlayTrigger trigger="click" placement="top" overlay={playRatePopover} rootClose>
      <button className={cx(styles.action, styles.speed)}>
        <i className="paperflite-circle-dashed-play"></i>
      </button>
    </OverlayTrigger>
  );
}

interface TranscriptsProps {
  simulation: LearningRecordSimulation;
}

export const Transcripts: React.FC<TranscriptsProps> = ({ simulation }) => {
  const courseContext = React.useContext(CourseContext);
  const transcripts = courseContext?.callRecording?.transcripts || [];
  const fetchingTranscripts = courseContext?.callRecording?.loading || false;

  if (fetchingTranscripts && _.size(transcripts) === 0) {
    return (
      <div className={styles.transcripts}>
        {_.times(4).map((_, index) => (
          <TranscriptRowPlaceholder key={index} />
        ))}
      </div>
    );
  }

  return (
    <div className={styles.transcripts}>
      {(transcripts || []).filter(t => t.message).map((transcript, index) => (
        <p key={index} className={styles.transcriptMessage}>
          {transcript.message}
        </p>
      ))}
    </div>
  );
};
